/** @prettier */

import {useEffect, useState} from 'react';
import {Grid, Popper, IconButton, IconButtonProps} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {DateView} from '@mui/x-date-pickers';
import {DesktopDatePicker} from '@mui/x-date-pickers/DesktopDatePicker';
import {AdapterLuxon} from '@mui/x-date-pickers/AdapterLuxon';
import {DateTime} from 'luxon';

//interface import
import * as apiIF from '../../../Type/api';
//type import
import {GraphDataRange} from '../../../Type/TypeDef';

import {iconBlack} from '../../../define';
import {getSettingUser} from '../../../API/API_Collection';
import {decideLuxonDatePickerLocale} from '../../../functions/decideLuxonDatePickerLocale';

/**
 * データ取得範囲の変更によるカレンダーの選択範囲変更関数
 * @param dateRange データ取得範囲
 * @returns 選択可能範囲
 */
const viewChanges = (dateRange: GraphDataRange): DateView[] => {
  switch (dateRange) {
    case 'day':
    default:
      return ['year', 'month', 'day'];
    case 'week':
      return ['year', 'month', 'day'];
    case 'month':
      return ['month', 'year'];
    case 'year':
      return ['year'];
  }
};

/**
 * カレンダーフォーマットの曜日パラメータを翻訳キーに変換
 * @param weekDay 曜日パラメータ
 * @returns 翻訳キー
 */
const convertWeekDay = (weekDay: Number) => {
  switch (weekDay) {
    case 1:
      return 'monday';
    case 2:
      return 'tuesday';
    case 3:
      return 'wednesday';
    case 4:
      return 'thursday';
    case 5:
    default:
      return 'friday';
    case 6:
      return 'saturday';
    case 7:
      return 'sunday';
  }
};

/**
 * 日時指定カレンダー設定
 * @param props props
 * @returns JSX.Element
 */
export const StaticDatePicker = (props: {
  /** グラフの開始日と終了日の設定 */
  setGraphDisplayDate: (start: string, end: string) => void;
  /** タイムゾーン */
  timeZone: string;
  /** 日時取得範囲 */
  dateRange: GraphDataRange;
}) => {
  const trans = useTranslation().t; //翻訳関数
  const {setGraphDisplayDate, timeZone, dateRange} = props;

  const now = DateTime.now().setZone(props.timeZone); //ユーザが選択したタイムゾーンの現在時刻
  const nowMidnight = now.startOf('day'); //ユーザが選択したタイムゾーンの現在日の00:00:00.000を取得
  const [selectDate, setSelectDate] = useState(nowMidnight); //選択した日付
  const [startDate, setStartDate] = useState(nowMidnight); //カレンダの開始日（初期値は現在日）
  const [endDate, setEndDate] = useState(nowMidnight.plus({day: 1})); //カレンダの終了日（初期値は現在日の翌日）
  //prettier-ignore
  const [language, setLanguage] = useState<apiIF.res.getSettingUser['language']>('english'); //言語情報

  /** グラフで表示できる下限の日付 */
  const minGraphDate = DateTime.fromObject(
    {
      year: 2020,
      month: 1,
      day: 1,
    },
    {zone: timeZone}
  );
  /** カレンダーで選択可能な範囲の開始日 */
  let minCalendarDate = now.minus({day: 3650});

  // 10年前の日付がグラフ表示できる下限の日付よりも前の場合
  if (minCalendarDate.toMillis() < minGraphDate.toMillis()) {
    minCalendarDate = minGraphDate;
  }
  /** カレンダーで選択可能な範囲の終了日 */
  const maxCalendarDate = now.startOf('day');

  //カレンダで選択した開始日、終了日をUTCに変換し、stateに保存
  useEffect(() => {
    const startTemp = startDate.setZone(timeZone).toUTC().toISO();
    const endTemp = endDate.setZone(timeZone).toUTC().toISO();
    if (startTemp === null || endTemp === null) return;
    setGraphDisplayDate(startTemp, endTemp);
  }, [startDate, endDate, timeZone]);

  //データ取得範囲切替時の処理
  useEffect(() => {
    switch (props.dateRange) {
      case 'tenYear':
        const year = now.year; //現在日の年を取得
        const newYear = nowMidnight.startOf('year'); //現在日の年の1月1日を取得
        if (year <= 2029) {
          //2020年以前のデータは取得できないので、現在の年が2029年以前の場合は2020年から現在の年までのデータを取得する
          const month = DateTime.fromObject(
            {
              year: 2020,
              month: 1,
              day: 1,
            },
            {zone: timeZone}
          );
          setStartDate(month);
          setEndDate(newYear.plus({year: 1}));
          setSelectDate(month);
        } else {
          //現在の年が2029年以降の場合は10年前から現在の年までのデータを取得する
          const month = nowMidnight.minus({year: 10}).startOf('year'); //現在日の10年前の1月1日を取得
          setStartDate(month);
          setEndDate(newYear.plus({year: 1}));
          setSelectDate(month);
        }
        break;
      case 'year':
        const month = nowMidnight.startOf('year'); //現在日の年の1月1日を取得
        setStartDate(month);
        setEndDate(month.plus({year: 1}));
        setSelectDate(month);
        break;
      case 'month':
        const date = nowMidnight.startOf('month'); //現在日の月の1日目を取得
        setStartDate(date);
        setEndDate(date.plus({month: 1}));
        setSelectDate(date);
        break;
      case 'week':
        setStartDate(nowMidnight.minus({day: 6}));
        setEndDate(nowMidnight.plus({day: 1}));
        setSelectDate(nowMidnight.minus({day: 6}));
        break;
      case 'day':
        setStartDate(nowMidnight);
        setEndDate(nowMidnight.plus({day: 1}));
        setSelectDate(nowMidnight);
        break;
    }
  }, [props.dateRange]);

  //言語切り替え時、言語情報更新
  useEffect(() => {
    getSettingUser().then(response => {
      setLanguage(response.language);
    });
  }, [trans]);

  /**
   * 日付選択時の処理
   * @param date カレンダーで選択した日付
   */
  const dateChange = (date: DateTime | null) => {
    //日付がおかしい場合
    if (date === null || date.isValid === false) return;
    const calendarDate = date.setZone(timeZone); //dayjsオブジェクトをdateオブジェクトに変換
    switch (props.dateRange) {
      case 'year':
      default:
        const month = calendarDate.startOf('year'); //選択日の年の1月1日を取得
        setStartDate(month);
        setEndDate(month.plus({year: 1}));
        setSelectDate(month);
        break;
      case 'month':
        const day = calendarDate.startOf('month'); //選択日の月の1日目を取得
        setStartDate(day);
        setEndDate(day.plus({month: 1}));
        setSelectDate(day);
        break;
      case 'week':
        setStartDate(calendarDate);
        setEndDate(calendarDate.plus({day: 7}));
        setSelectDate(calendarDate);
        break;
      case 'day':
        setStartDate(calendarDate);
        setEndDate(calendarDate.plus({day: 1}));
        setSelectDate(calendarDate);
        break;
    }
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={decideLuxonDatePickerLocale(language)}>
      <DesktopDatePicker
        value={selectDate}
        views={viewChanges(dateRange)}
        minDate={minCalendarDate}
        maxDate={maxCalendarDate}
        onChange={date => dateChange(date)}
        disabled={dateRange === 'tenYear'}
        slots={{
          popper: props => (
            <Popper
              {...props}
              className="graph__card__main__menu__datePicker__calendar"
            />
          ),
        }}
        format="dd/MM/yyyy"
        dayOfWeekFormatter={(nameOfDay, dayInfo) => {
          return trans(
            `DatePicker.calendarFormat.day.${convertWeekDay(dayInfo.weekday)}`
          );
        }}
        className="graph__card__main__menu__datePicker"
      />
    </LocalizationProvider>
  );
};

/**
 * 比較データ用日時指定カレンダー設定
 * @param props props
 * @returns JSX.Element
 */
export const StaticComparisonDatePicker = (props: {
  /** グラフの開始日と終了日の設定 */
  setGraphDisplayDate: (start: string, end: string) => void;
  /** タイムゾーン */
  timeZone: string;
  /** 日時取得範囲 */
  dateRange: GraphDataRange;
  /** 閉じるボタン押下時の処理 */
  onCloseClick?: IconButtonProps['onClick'];
}) => {
  const trans = useTranslation().t; //翻訳関数
  const {setGraphDisplayDate, timeZone, dateRange} = props;

  const now = DateTime.now().setZone(props.timeZone); //ユーザが選択したタイムゾーンの現在時刻
  const nowMidnight = now.startOf('day'); //ユーザが選択したタイムゾーンの現在日の00:00:00.000を取得
  const [selectDate, setSelectDate] = useState(nowMidnight.minus({day: 1})); //選択した日付
  const [startDate, setStartDate] = useState(nowMidnight.minus({day: 1})); //カレンダの開始日（初期値は現在日の1日前）
  const [endDate, setEndDate] = useState(nowMidnight); //カレンダの終了日（初期値は現在日）
  //prettier-ignore
  const [language, setLanguage] = useState<apiIF.res.getSettingUser['language']>('english'); //言語情報

  /** グラフで表示できる下限の日付 */
  const minGraphDate = DateTime.fromObject(
    {year: 2020, month: 1, day: 1},
    {zone: timeZone}
  );
  /** カレンダーで選択可能な範囲の開始日*/
  let minCalendarDate = now.minus({day: 3650});

  // 10年前の日付がグラフ表示できる下限の日付よりも前の場合
  if (minCalendarDate.toMillis() < minGraphDate.toMillis()) {
    minCalendarDate = minGraphDate;
  }
  /** カレンダーで選択可能な範囲の終了日（文字列） */
  const maxCalendarDate = now.startOf('day');

  //カレンダで選択した開始日、終了日をUTCに変換し、stateに保存
  useEffect(() => {
    const startTemp = startDate.setZone(timeZone).toUTC().toISO();
    const endTemp = endDate.setZone(timeZone).toUTC().toISO();
    if (startTemp === null || endTemp === null) return;
    setGraphDisplayDate(startTemp, endTemp);
  }, [startDate, endDate, timeZone]);

  //データ取得範囲切替時の処理
  useEffect(() => {
    switch (props.dateRange) {
      case 'year':
        const month = nowMidnight.startOf('year'); //現在日の年の1月1日を取得
        setStartDate(month.minus({year: 1}));
        setEndDate(month);
        setSelectDate(month.minus({year: 1}));
        break;
      case 'month':
        const date = nowMidnight.startOf('month'); //現在日の月の1日目を取得
        setStartDate(date.minus({month: 1}));
        setEndDate(date);
        setSelectDate(date.minus({month: 1}));
        break;
      case 'week':
        setStartDate(nowMidnight.minus({day: 13}));
        setEndDate(nowMidnight.minus({day: 6}));
        setSelectDate(nowMidnight.minus({day: 13}));
        break;
      case 'day':
        setStartDate(nowMidnight.minus({day: 1}));
        setEndDate(nowMidnight);
        setSelectDate(nowMidnight.minus({day: 1}));
        break;
    }
  }, [props.dateRange]);

  //言語切り替え時、言語情報更新
  useEffect(() => {
    getSettingUser().then(response => {
      setLanguage(response.language);
    });
  }, [trans]);

  /**
   * 日付選択時の処理
   * @param date カレンダーで選択した日付
   */
  const dateChange = (date: DateTime | null) => {
    if (date === null) return;
    const calendarDate = date.setZone(timeZone);
    switch (props.dateRange) {
      case 'year':
      default:
        const month = calendarDate.startOf('year'); //選択日の年の1月1日を取得
        setStartDate(month);
        setEndDate(month.plus({year: 1}));
        setSelectDate(month);
        break;
      case 'month':
        const day = calendarDate.startOf('month'); //選択日の月の1日目を取得
        setStartDate(day);
        setEndDate(day.plus({month: 1}));
        setSelectDate(day);
        break;
      case 'week':
        setStartDate(calendarDate);
        setEndDate(calendarDate.plus({day: 6}));
        setSelectDate(calendarDate);
        break;
      case 'day':
        setStartDate(calendarDate);
        setEndDate(calendarDate.plus({day: 1}));
        setSelectDate(calendarDate);
        break;
    }
  };

  return (
    <>
      <Grid container alignItems="center">
        <Grid item xs="auto">
          <LocalizationProvider
            dateAdapter={AdapterLuxon}
            adapterLocale={decideLuxonDatePickerLocale(language)}>
            <DesktopDatePicker
              label={`${trans('DatePicker.calendarFormat.label')}`}
              value={selectDate}
              views={viewChanges(dateRange)}
              minDate={minCalendarDate}
              maxDate={maxCalendarDate}
              onChange={date => dateChange(date)}
              slots={{
                popper: props => (
                  <Popper
                    {...props}
                    className="graph__card__main__menu__datePicker__calendar"
                  />
                ),
              }}
              format="dd/MM/yyyy"
              dayOfWeekFormatter={(nameOfDay, dayInfo) =>
                trans(
                  `DatePicker.calendarFormat.day.${convertWeekDay(
                    dayInfo.weekday
                  )}`
                )
              }
              className="graph__card__main__menu__datePicker"
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs>
          {/* 比較用カレンダー削除ボタン */}
          <IconButton onClick={props.onCloseClick}>
            <img
              src={iconBlack.CLOSE.src}
              alt={`${trans(
                `DatePicker.image.alternativeText.${iconBlack.CLOSE.alt}`
              )}`}
              className="graph__card__main__menu__icon"
            />
          </IconButton>
        </Grid>
      </Grid>
    </>
  );
};
